'use client';

import Image from 'next/image';
import { Button } from "@/components/ui/button";
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

interface CenteredQuoteSectionProps {
  subtitle?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  citation: {
    text: string;
    author: string;
    role: string;
    avatar: string;
    company?: string;
  };
}

export function CenteredQuoteSection({
  subtitle,
  title,
  description,
  buttonText,
  buttonLink,
  citation
}: CenteredQuoteSectionProps) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          className="flex flex-col items-center text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {subtitle && (
            <motion.h3 
              className="text-sm font-heading font-semibold text-primary uppercase tracking-wide mb-2"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {subtitle}
            </motion.h3>
          )}
          {title && (
            <motion.h2 
              className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {title}
            </motion.h2>
          )}
          {description && (
            <motion.p 
              className="text-lg font-sans text-gray-600 leading-relaxed mb-12"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {description}
            </motion.p>
          )}
          
          <motion.div 
            className="relative bg-gray-50 rounded-lg p-8 shadow-sm w-full mb-8 group hover:bg-gray-100 transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Quote className="absolute text-gray-200 w-12 h-12 -top-6 -left-6 group-hover:text-primary/20 transition-colors duration-300" />
            
            <div className="flex items-start gap-6">
              {/* Avatar Circle */}
              <motion.div 
                className="flex-shrink-0 w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Image
                  src={citation.avatar}
                  alt={citation.author}
                  width={96}
                  height={96}
                  className="rounded-full"
                />
              </motion.div>

              {/* Quote Content */}
              <div className="flex-1 font-sans">
                <p className="text-xl text-gray-700 font-medium mb-3">
                  &ldquo;{citation.text}&rdquo;
                </p>
                <div className="text-base text-gray-500">
                  <span className="font-heading font-medium">{citation.author}</span>
                  <span className="ml-2">{citation.role}</span>
                  {citation.company && (
                    <span className="ml-2">· {citation.company}</span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {buttonText && buttonLink && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Button 
                variant="outline" 
                size="lg" 
                className="font-sans" 
                asChild
              >
                <motion.a 
                  href={buttonLink}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {buttonText}
                </motion.a>
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
} 